/**
 * Performance view for admin level test report: levels with questions and accuracy.
 * Uses timeline AnswerSubmitted events grouped by level and optional performanceSummary from API.
 */

import type { LevelTestSession } from "./studentLevelTest.interfaces";
import { buildTimelineFromSession, type AnswerSubmittedEvent } from "./levelTestTimeline";

export interface PerformanceLevelSection {
  level: number;
  accuracyPercent: number;
  questions: AnswerSubmittedEvent[];
  isWorst: boolean;
  unlockedModules: Array<{ id: string; name?: string; internalName?: string }>;
  /** True when the section represents baseline unlocks from a skipped test (no per-question stats). */
  isSkipPlacement?: boolean;
}

/**
 * Builds performance view: one section per level with questions (same shape as timeline) and accuracy.
 * When performanceSummary is present, uses its perLevelStats and worstLevel; otherwise derives from answers.
 */
export function buildPerformanceViewFromSession(
  session: LevelTestSession | null
): PerformanceLevelSection[] {
  if (!session) return [];

  if (session.status === "Skipped" && session.performanceSummary) {
    const summary = session.performanceSummary;
    return [
      {
        level: summary.worstLevel ?? 1,
        accuracyPercent: 0,
        questions: [],
        isWorst: true,
        unlockedModules: summary.unlockedModules ?? [],
        isSkipPlacement: true,
      },
    ];
  }

  if (!session.answers?.length) return [];

  const timeline = buildTimelineFromSession(session);
  const answerEvents = timeline.filter(
    (e): e is AnswerSubmittedEvent => e.type === "AnswerSubmitted"
  );

  const byLevel = new Map<number, AnswerSubmittedEvent[]>();
  for (const ev of answerEvents) {
    const level = ev.level ?? 1;
    if (!byLevel.has(level)) byLevel.set(level, []);
    byLevel.get(level)!.push(ev);
  }

  const levels = Array.from(byLevel.keys()).sort((a, b) => a - b);
  const summary = session.performanceSummary;
  const worstLevel = summary?.worstLevel ?? deriveWorstLevel(byLevel);
  const unlockedModules = summary?.unlockedModules ?? [];
  const statsByLevel = new Map(
    summary?.perLevelStats?.map((s) => [s.level, s]) ?? []
  );

  return levels.map((level) => {
    const questions = byLevel.get(level) ?? [];
    const stat = statsByLevel.get(level);
    const accuracyPercent =
      stat != null
        ? stat.accuracyPercent
        : questions.length > 0
          ? Math.round(
              (100 * questions.filter((q) => q.isCorrect).length) /
                questions.length
            )
          : 0;

    return {
      level,
      accuracyPercent,
      questions,
      isWorst: level === worstLevel,
      unlockedModules: level === worstLevel ? unlockedModules : [],
    };
  });
}

function deriveWorstLevel(
  byLevel: Map<number, AnswerSubmittedEvent[]>
): number {
  let worstLevel = 1;
  let lowestRate = Infinity;
  for (const [level, questions] of byLevel) {
    if (questions.length === 0) continue;
    const correct = questions.filter((q) => q.isCorrect).length;
    const rate = correct / questions.length;
    if (rate < lowestRate || (rate === lowestRate && level < worstLevel)) {
      lowestRate = rate;
      worstLevel = level;
    }
  }
  return worstLevel;
}

export function hasPerformanceData(session: LevelTestSession | null): boolean {
  if (!session) return false;
  if (session.answers?.length) return true;
  return session.status === "Skipped" && session.performanceSummary != null;
}
